import axios from 'axios';
import type { AxiosPromise } from 'axios';
import type { ICartRequest, ICartItemRequest } from '@/data/_autogen/IValveVista';

const API_BASE_URL = process.env.VUE_APP_API_URL || '/api';
const USE_MOCK_API = false;

export default class CartService {
  /**
   * Add an item to the shopping cart
   * POST /api/v1/shoppingcart/add-item
   * @param item - Cart item to add
   * @returns Promise resolving to the added item with cartId populated
   */
  static addItemToCart(
    item: ICartItemRequest
  ): AxiosPromise<ICartItemRequest> {
    if (USE_MOCK_API) {
      return this.mockAddItemToCart(item);
    }

    return axios.post<ICartItemRequest>(
      `${API_BASE_URL}/v1/cart/add-item`,
      item
    );
  }

  static updateItemQuantity( item:ICartItemRequest) {
    if (USE_MOCK_API) {
      return;
    }
    return axios.post<ICartItemRequest>(
        `${API_BASE_URL}/v1/cart/update-item-quantity`,
        item
    );
  }

  /**
   * Remove an item from the shopping cart
   * POST /api/v1/shoppingcart/remove-item
   * @param item - Cart item to remove
   * @returns Promise that resolves when item is removed
   */
  static removeItemFromCart(
    item: ICartItemRequest
  ): AxiosPromise<void> {
    if (USE_MOCK_API) {
      return this.mockRemoveItemFromCart(item);
    }

    return axios.post<void>(
      `${API_BASE_URL}/v1/cart/remove-item`,
      item
    );
  }

  /**
   * Get cart contents by ID
   * GET /api/v1/shoppingcart/{cartId}
   * @param cartId - ID of the cart to retrieve
   * @returns Promise resolving to cart contents
   */
  static getCartById(cartId: number): AxiosPromise<ICartRequest> {
    if (USE_MOCK_API) {
      return this.mockGetCartById(cartId);
    }

    return axios.get<ICartRequest>(
      `${API_BASE_URL}/v1/cart/${cartId}`
    );
  }

  // Mock implementations for development
  private static mockAddItemToCart(
    item: ICartItemRequest
  ): AxiosPromise<ICartItemRequest> {
    return new Promise((resolve) => {
      setTimeout(() => {
        // Simulate API response with cartId
        const response = {
          ...item,
          cartId: item.cartId || Math.floor(Math.random() * 10000),
          id: Math.floor(Math.random() * 100000),
        };
        resolve({
          data: response,
          status: 201,
          statusText: 'Created',
          headers: {},
          config: {} as any,
        });
      }, 500);
    });
  }

  private static mockRemoveItemFromCart(
    item: ICartItemRequest
  ): AxiosPromise<void> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          data: undefined,
          status: 200,
          statusText: 'OK',
          headers: {},
          config: {} as any,
        });
      }, 300);
    });
  }

  private static mockGetCartById(cartId: number): AxiosPromise<ICartRequest> {
    return new Promise((resolve) => {
      setTimeout(() => {
        const response: ICartRequest = {
          id: cartId,
          cartItems: [],
        };
        resolve({
          data: response,
          status: 200,
          statusText: 'OK',
          headers: {},
          config: {} as any,
        });
      }, 400);
    });
  }
}
